import React, { useState, useEffect, useCallback } from 'react';
import supabase, { orgId } from '../supabaseClient';

const ALL_TABS_LIST = [
  { key: 'portfolio',  label: 'Exec Summary' },
  { key: 'output',     label: 'Game Output' },
  { key: 'pnl',        label: 'Fin Statements', children: [
    { key: 'pnl_pl',   label: 'P&L' },
    { key: 'pnl_cf',   label: 'Cash Flow' },
  ]},
  { key: 'revenue',    label: 'Game Inputs' },
  { key: 'people',     label: 'Overheads' },
  { key: 'cash',       label: 'Cash' },
  { key: 'standing',   label: 'Standing Data' },
];

// Flat list for table headers (expand children)
function flatTabs(tabs) {
  const result = [];
  tabs.forEach(t => {
    result.push(t);
    if (t.children) t.children.forEach(c => result.push({ ...c, isChild: true, parentKey: t.key }));
  });
  return result;
}
const FLAT_TABS = flatTabs(ALL_TABS_LIST);

const BIZ_MODELS = ['Publisher', 'IP Development', 'Co-Dev Studio', 'F2P Mobile', 'Consumer App', 'Other'];

function ukDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${String(d.getFullYear()).slice(2)}`;
}

function isLvlUp(email) {
  return (email || '').toLowerCase().includes('lvlup.partners');
}

// ── ADMINISTRATORS TAB ────────────────────────────────────────────────────────
function AdministratorsTab({ session }) {
  const [users, setUsers]             = useState([]);
  const [userTypes, setUserTypes]     = useState([]); // [{id, name, canEdit, tabs: {tabKey: bool}}]
  const [userAssignments, setUserAssignments] = useState({}); // {userId: {typeId, tabOverrides: {tabKey: bool}}}
  const [entities, setEntities]       = useState([]);
  const [newEntity, setNewEntity]     = useState('');
  const [newEntityModel, setNewEntityModel] = useState('Publisher');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteStatus, setInviteStatus] = useState('');
  const [newTypeName, setNewTypeName] = useState('');
  const [loading, setLoading]         = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/getUsers', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestingUserId: session.user.id, orgId }),
      });
      const data = await res.json();
      setUsers(data.users || []);
    } catch (_) {
      const { data } = await supabase.from('user_roles').select('user_id, role, created_at');
      setUsers(data || []);
    }

    const { data: entData } = await supabase.from('entities').select('id, name, biz_model').eq('org_id', orgId).order('name');
    setEntities(entData || []);

    const { data: utData } = await supabase.from('app_settings').select('value').eq('org_id', orgId).eq('key', 'user_types').maybeSingle();
    if (utData?.value) try { setUserTypes(JSON.parse(utData.value) || []); } catch (_) {}

    const { data: uaData } = await supabase.from('app_settings').select('value').eq('org_id', orgId).eq('key', 'user_assignments').maybeSingle();
    if (uaData?.value) try { setUserAssignments(JSON.parse(uaData.value) || {}); } catch (_) {}

    setLoading(false);
  }, [session.user.id]);

  useEffect(() => { load(); }, [load]);

  const saveUserTypes = async (next) => {
    setUserTypes(next);
    await supabase.from('app_settings').upsert({ org_id: orgId, key: 'user_types', value: JSON.stringify(next) }, { onConflict: 'org_id,key' });
  };

  const saveUserAssignments = async (next) => {
    setUserAssignments(next);
    await supabase.from('app_settings').upsert({ org_id: orgId, key: 'user_assignments', value: JSON.stringify(next) }, { onConflict: 'org_id,key' });
  };

  const addUserType = () => {
    if (!newTypeName.trim()) return;
    const next = [...userTypes, {
      id: Math.random().toString(36).slice(2),
      name: newTypeName.trim(), canEdit: false,
      tabs: Object.fromEntries(ALL_TABS_LIST.map(t => [t.key, true])),
    }];
    saveUserTypes(next);
    setNewTypeName('');
  };

  const updateTypeTab = (typeId, tabKey, val) => {
    const next = userTypes.map(t => t.id === typeId ? { ...t, tabs: { ...t.tabs, [tabKey]: val } } : t);
    saveUserTypes(next);
  };

  const updateTypeEdit = (typeId, val) => {
    const next = userTypes.map(t => t.id === typeId ? { ...t, canEdit: val } : t);
    saveUserTypes(next);
  };

  const removeType = (typeId) => saveUserTypes(userTypes.filter(t => t.id !== typeId));

  const updateUserType = (userId, typeId) => {
    const next = { ...userAssignments, [userId]: { ...(userAssignments[userId] || {}), typeId } };
    saveUserAssignments(next);
  };

  const updateUserTabOverride = (userId, tabKey, val) => {
    const next = { ...userAssignments, [userId]: { ...(userAssignments[userId] || {}), tabOverrides: { ...(userAssignments[userId]?.tabOverrides || {}), [tabKey]: val } } };
    saveUserAssignments(next);
  };

  async function handleInvite(e) {
    e.preventDefault();
    setInviteStatus('sending');
    try {
      const res = await fetch('/api/inviteUser', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail, role: 'viewer', requestingUserId: session.user.id, orgId }),
      });
      const data = await res.json();
      if (data.error) setInviteStatus('Error: ' + data.error);
      else { setInviteStatus('Invite sent to ' + inviteEmail); setInviteEmail(''); load(); }
    } catch (err) { setInviteStatus('Error: ' + err.message); }
    setTimeout(() => setInviteStatus(''), 4000);
  }

  async function handleDeleteUser(userId, email) {
    if (!window.confirm(`Revoke access for ${email}?`)) return;
    try {
      await fetch('/api/deleteUser', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUserId: userId, requestingUserId: session.user.id, orgId }) });
      load();
    } catch (err) { alert('Error: ' + err.message); }
  }

  async function addEntity() {
    if (!newEntity.trim()) return;
    await supabase.from('entities').insert({ org_id: orgId, name: newEntity.trim(), biz_model: newEntityModel });
    setNewEntity('');
    load();
  }

  async function removeEntity(id) {
    await supabase.from('entities').delete().eq('org_id', orgId).eq('id', id);
    load();
  }

  const th = { fontSize: 9, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--t4)', padding: '6px 8px', background: 'var(--s1)', textAlign: 'center', whiteSpace: 'nowrap' };

  if (loading) return <p className="nomsg">Loading…</p>;

  return (
    <div>
      {/* Entities */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head"><span className="card-title">Entities</span></div>
        <div className="card-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
            {entities.map(e => (
              <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ flex: 1, fontSize: 13, color: 'var(--t1)' }}>{e.name}</span>
                <span style={{ fontSize: 11, color: 'var(--t3)', background: 'var(--s2)', border: '1px solid var(--e1)', borderRadius: 'var(--r)', padding: '2px 8px' }}>{e.biz_model || '—'}</span>
                <button className="rm" onClick={() => removeEntity(e.id)}>×</button>
              </div>
            ))}
            {entities.length === 0 && <p style={{ fontSize: 12, color: 'var(--t3)' }}>No entities added yet.</p>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input className="auth-input" style={{ flex: 1, maxWidth: 220 }} placeholder="Entity name…"
              value={newEntity} onChange={e => setNewEntity(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEntity(); }}} />
            <select value={newEntityModel} onChange={e => setNewEntityModel(e.target.value)}>
              {BIZ_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <button className="action-btn secondary" onClick={addEntity}>+ Add</button>
          </div>
        </div>
      </div>

      {/* User Types */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head"><span className="card-title">User Types & Tab Permissions</span><span className="card-meta">Admins always see everything</span></div>
        <div className="card-body" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left', minWidth: 120 }}>User Type</th>
                  <th style={th}>Can Edit</th>
                  {FLAT_TABS.map(t => (
                    <th key={t.key} style={{ ...th, minWidth: 60, fontSize: 8,
                      paddingLeft: t.isChild ? 12 : undefined,
                      color: t.isChild ? 'var(--t4)' : undefined }}>
                      {t.isChild ? '↳ ' : ''}{t.label}
                    </th>
                  ))}
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {userTypes.map(ut => (
                  <tr key={ut.id} style={{ borderBottom: '1px solid var(--e0)' }}>
                    <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--t1)', fontWeight: 500 }}>{ut.name}</td>
                    <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                      <input type="checkbox" checked={ut.canEdit} onChange={e => updateTypeEdit(ut.id, e.target.checked)} />
                    </td>
                    {FLAT_TABS.map(t => (
                      <td key={t.key} style={{ textAlign: 'center', padding: '6px 8px' }}>
                        <input type="checkbox"
                          checked={ut.tabs?.[t.key] ?? true}
                          onChange={e => updateTypeTab(ut.id, t.key, e.target.checked)} />
                      </td>
                    ))}
                    <td style={{ padding: '6px 8px' }}>
                      <button className="rm" onClick={() => removeType(ut.id)}>×</button>
                    </td>
                  </tr>
                ))}
                {userTypes.length === 0 && (
                  <tr><td colSpan={ALL_TABS_LIST.length + 3} className="nomsg">No user types defined yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '10px 16px', display: 'flex', gap: 8 }}>
            <input className="auth-input" style={{ flex: 1, maxWidth: 200 }} placeholder="New type name…"
              value={newTypeName} onChange={e => setNewTypeName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addUserType(); }}} />
            <button className="action-btn secondary" onClick={addUserType}>+ Add Type</button>
          </div>
        </div>
      </div>

      {/* Users */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head"><span className="card-title">Users</span><span className="card-meta">{users.length} user{users.length !== 1 ? 's' : ''}</span></div>
        <div className="card-body" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }}>Email</th>
                  <th style={th}>User Type</th>
                  {FLAT_TABS.map(t => (
                    <th key={t.key} style={{ ...th, minWidth: 50, fontSize: 8,
                      paddingLeft: t.isChild ? 12 : undefined }}>
                      {t.isChild ? '↳ ' : ''}{t.label}
                    </th>
                  ))}
                  <th style={th}>Joined</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const assignment = userAssignments[u.user_id] || {};
                  const uType = userTypes.find(t => t.id === assignment.typeId);
                  return (
                    <tr key={u.user_id} style={{ borderBottom: '1px solid var(--e0)' }}>
                      <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--t1)' }}>{u.email || u.user_id.slice(0,8) + '…'}</td>
                      <td style={{ padding: '6px 8px' }}>
                        <select value={assignment.typeId || ''} onChange={e => updateUserType(u.user_id, e.target.value)}
                          style={{ fontSize: 11, padding: '3px 20px 3px 6px' }}>
                          <option value="">— type —</option>
                          {userTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </td>
                      {FLAT_TABS.map(t => {
                        const typeDefault = uType?.tabs?.[t.key] ?? true;
                        const override = assignment.tabOverrides?.[t.key];
                        const effective = override !== undefined ? override : typeDefault;
                        return (
                          <td key={t.key} style={{ textAlign: 'center', padding: '6px 8px' }}>
                            <input type="checkbox" checked={effective}
                              title={override !== undefined ? 'Override' : 'From type'}
                              style={{ accentColor: override !== undefined ? 'var(--amber)' : 'var(--blue)' }}
                              onChange={e => updateUserTabOverride(u.user_id, t.key, e.target.checked)} />
                          </td>
                        );
                      })}
                      <td style={{ padding: '6px 8px', fontSize: 11, color: 'var(--t3)' }}>{ukDate(u.created_at)}</td>
                      <td style={{ padding: '6px 8px' }}>
                        {u.user_id !== session.user.id && (
                          <button className="rm" onClick={() => handleDeleteUser(u.user_id, u.email)}>×</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Invite */}
      <div className="card">
        <div className="card-head"><span className="card-title">Invite User</span></div>
        <div className="card-body">
          <form onSubmit={handleInvite} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input type="email" className="auth-input" style={{ maxWidth: 280 }} placeholder="email@example.com"
              value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} required />
            <button type="submit" className="action-btn primary">Send Invite</button>
          </form>
          {inviteStatus && <p style={{ marginTop: 8, fontSize: 12, color: inviteStatus.startsWith('Error') ? 'var(--red)' : 'var(--green)' }}>{inviteStatus}</p>}
        </div>
      </div>
    </div>
  );
}

// ── LVL UP TAB ─────────────────────────────────────────────────────────────────
function LvlUpTab({ session }) {
  const [users, setUsers] = useState([]);
  const [pricing, setPricing] = useState({}); // {userId: amount}
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch('/api/getUsers', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ requestingUserId: session.user.id, orgId }),
        });
        const data = await res.json();
        setUsers((data.users || []).filter(u => !isLvlUp(u.email)));
      } catch (_) {
        const { data } = await supabase.from('user_roles').select('user_id, role, created_at, email');
        setUsers((data || []).filter(u => !isLvlUp(u.email)));
      }
      const { data: pData } = await supabase.from('app_settings').select('value').eq('org_id', orgId).eq('key', 'lvlup_pricing').maybeSingle();
      if (pData?.value) try { setPricing(JSON.parse(pData.value) || {}); } catch (_) {}
      setLoading(false);
    }
    load();
  }, [session.user.id]);

  const updatePrice = async (userId, val) => {
    const next = { ...pricing, [userId]: val };
    setPricing(next);
    await supabase.from('app_settings').upsert({ org_id: orgId, key: 'lvlup_pricing', value: JSON.stringify(next) }, { onConflict: 'org_id,key' });
  };

  const total = users.reduce((s, u) => s + (parseFloat(pricing[u.user_id]) || 0), 0);

  if (loading) return <p className="nomsg">Loading…</p>;

  return (
    <div>
      <div className="card">
        <div className="card-head">
          <span className="card-title">Client Users & Licensing</span>
          <span className="card-meta">Excludes @lvlup.partners</span>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <table className="admin-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Email</th>
                <th style={{ textAlign: 'left' }}>Joined</th>
                <th style={{ textAlign: 'right', minWidth: 160 }}>Monthly License Fee</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id}>
                  <td style={{ fontSize: 13, color: 'var(--t1)' }}>{u.email || u.user_id.slice(0,8) + '…'}</td>
                  <td style={{ fontSize: 12, color: 'var(--t3)' }}>{ukDate(u.created_at)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                      <span style={{ fontSize: 12, color: 'var(--t3)' }}>£</span>
                      <input type="number" step="0.01" min="0"
                        value={pricing[u.user_id] || ''}
                        onChange={e => updatePrice(u.user_id, e.target.value)}
                        placeholder="0.00"
                        style={{ width: 90, textAlign: 'right', background: 'var(--s2)', border: '1px solid var(--e1)',
                          borderRadius: 'var(--r)', padding: '4px 8px', fontSize: 12, color: 'var(--t1)', outline: 'none' }} />
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={3} className="nomsg">No client users found.</td></tr>}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--e1)', background: 'var(--s1)' }}>
                <td colSpan={2} style={{ padding: '8px 10px', fontSize: 12, fontWeight: 600, color: 'var(--t1)' }}>Total Monthly Recurring Revenue</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 14, fontWeight: 700, color: 'var(--green)' }}>
                  £{total.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── MAIN EXPORT ───────────────────────────────────────────────────────────────
export default function AdminPanel({ session, activeSubTab = 'admin', onSubTabChange }) {
  return (
    <div>
      {activeSubTab === 'admin' && <AdministratorsTab session={session} />}
      {activeSubTab === 'lvlup' && isLvlUp(session?.user?.email) && <LvlUpTab session={session} />}
      {activeSubTab === 'lvlup' && !isLvlUp(session?.user?.email) && (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>
          Access restricted to Lvl Up Partners staff.
        </div>
      )}
    </div>
  );
}

// Export whether current user is LvlUp for App.js
export { isLvlUp };
